import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import ReactGA from "react-ga4";
import fetch from "../../auth/FetchInterceptor.js";
import { END_POINTS } from "constants/APIConstant.js";
import { apiResponseMessage } from "redux/actions/ApiMessageResponse.js";
import { MESSAGE_TYPE } from "redux/constants/ApiMessageResponse.js";
import { nationalCenterSchema } from "./nationalCenterSchema";
import { buildTrainingPayload } from "./utils";

export function useNationalCenterForm() {
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);
  const dispatch = useDispatch();

  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(nationalCenterSchema),
    defaultValues: {
      governorate: "1",
      workSource: "1",
      trainingProgramId: "1",
      workWithStatistics: "1",
      computerUsageQuality: "1",
      spssUsageQuality: "1",
    },
  });
  const { errors } = formState;

  const governoratesData = useSWR({ url: END_POINTS.GOVERNORATES });
  const getTrainingProgram = useSWR({
    url: END_POINTS.TRAINING_PROGRAM_LOOKUP,
  });
  const getServices = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS}/${END_POINTS.NATIONAL_CENTER_ID}`,
  });
  const getDepartment = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS_DEPARTMENT}=${END_POINTS.NATIONAL_CENTER_ID}`,
  });

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
    ReactGA.send({
      hitType: "pageview",
      page: "/nationalCenter",
      title: "NationalCenter Page",
    });
  }, []);

  async function postNationalCenter(data) {
    return fetch.post(
      END_POINTS.TRAINING_PROGRAM_ADD,
      buildTrainingPayload(data),
    );
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await postNationalCenter(data);
      if (result?.state) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="sent-successfully" />,
            messageType: MESSAGE_TYPE.SUCCESS,
          }),
        );
      } else {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: result?.data?.message ? (
              result?.data?.message
            ) : (
              <FormattedMessage id="timeout-error" />
            ),
            messageType: MESSAGE_TYPE.ERROR,
          }),
        );
      }
    } catch (error) {
      dispatch(
        apiResponseMessage({
          showMessage: true,
          message: error?.response?.data?.message ? (
            error?.response?.data?.message
          ) : (
            <FormattedMessage id="timeout-error" />
          ),
          messageType: MESSAGE_TYPE.ERROR,
        }),
      );
    }
  });

  return {
    dir,
    currentLocale,
    register,
    errors,
    onSubmit,
    governorates: governoratesData?.data?.data || [],
    trainingPrograms: getTrainingProgram?.data?.data || [],
    getDepartment,
    getServices,
    isLoading:
      getDepartment.isLoading ||
      getServices.isLoading ||
      getTrainingProgram.isLoading,
  };
}
